// /components/games/MemoryMatch.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Paper,
  CssBaseline
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const emojiSet = ["🐍", "🚀", "🧠", "🎮", "💎", "⚡", "🔥", "🎯"];

const MemoryMatch = () => {
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [themeMode, setThemeMode] = useState("light");

  const theme = createTheme({
    palette: {
      mode: themeMode,
      primary: { main: themeMode === "light" ? "#1976d2" : "#64b5f6" },
      background: {
        default: themeMode === "light" ? "#f4f7fa" : "#0d1117",
        paper: themeMode === "light" ? "#fff" : "#161b22",
      },
    },
    typography: { fontFamily: "Inter, sans-serif" },
  });

  useEffect(() => {
    resetGame();
  }, []);

  const resetGame = () => {
    const doubled = [...emojiSet, ...emojiSet];
    const shuffled = doubled.sort(() => Math.random() - 0.5);
    setCards(shuffled);
    setFlipped([]);
    setMatched([]);
    setMoves(0);
  };

  const handleFlip = (index) => {
    if (flipped.length === 2 || flipped.includes(index) || matched.includes(index)) return;

    const newFlipped = [...flipped, index];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setMoves((m) => m + 1);
      const [first, second] = newFlipped;
      if (cards[first] === cards[second]) {
        setMatched((prev) => [...prev, first, second]);
      }
      setTimeout(() => setFlipped([]), 800);
    }
  };

  const isGameOver = matched.length === cards.length;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            themeMode === "light"
              ? "linear-gradient(135deg,#f0f4ff,#c3d9ff)"
              : "linear-gradient(135deg,#0f2027,#203a43,#2c5364)",
          transition: "0.3s",
          py: 6,
          px: 2,
        }}
      >
        <Card sx={{ p: 3, borderRadius: 4, boxShadow: 8 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" fontWeight="bold">
                🧠 Memory Match
              </Typography>
              <Button
                onClick={() =>
                  setThemeMode(themeMode === "light" ? "dark" : "light")
                }
              >
                {themeMode === "light" ? "Dark" : "Light"}
              </Button>
            </Box>

            <Grid container spacing={1} justifyContent="center" maxWidth="xs">
              {cards.map((emoji, index) => {
                const isFlipped = flipped.includes(index) || matched.includes(index);
                return (
                  <Grid item xs={3} key={index}>
                    <Paper
                      onClick={() => handleFlip(index)}
                      sx={{
                        height: 70,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 32,
                        borderRadius: 2,
                        cursor: "pointer",
                        backgroundColor: isFlipped ? "primary.main" : "grey.400",
                        color: isFlipped ? "#fff" : "transparent",
                        transition: "all 0.3s",
                        transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                      }}
                    >
                      {emoji}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            <Typography align="center" mt={2}>
              Moves: {moves}
            </Typography>

            {isGameOver && (
              <Typography align="center" mt={1} color="success.main">
                🎉 All matched in {moves} moves!
              </Typography>
            )}

            <Box textAlign="center" mt={2}>
              <Button variant="outlined" onClick={resetGame}>
                Restart
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
};

export default MemoryMatch;
